import clsx from 'clsx';
import SignificanceBadge from './SignificanceBadge';

interface CellData {
  count: number;
  total: number;
  rate: number;
  controlRate: number;
  controlCount: number;
  controlTotal: number;
  difference: number;
  hasDisparity: boolean;
  /** Equal values, or a t-test with one loan on a side: nothing to compare (black NA, not green). */
  noComparison?: boolean;
  significanceLevel: string;
  riskLevel: string;
  pValue: number | null;
  testUsed: string;
  sampleSizeWarning: boolean;
  protectedGroup: string;
  loanPurpose: string;
}

interface FldCellBlockProps {
  cell: CellData;
  isMeanComparison?: boolean;
  /** Decimal places for mean comparison values (default: 3; use 0 for whole numbers like processing days) */
  meanDecimals?: number;
  /** Unit suffix for mean comparison values (e.g., "avg. days" for U2 processing time). */
  meanUnit?: string;
  /** Format mean values as USD currency (e.g., "$1,170.75") — used for P4 OC / P4 TLC per Wallace 2026-05-07. */
  meanCurrency?: boolean;
  /** Custom labels for rate vs control (default: "PBG" / "CG") */
  rateLabel?: string;
  controlLabel?: string;
  onClick?: () => void;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Round to N decimals using half-up.
 *
 * JavaScript's Number.toFixed uses IEEE 754 representation, which can
 * incorrectly round boundary values DOWN — e.g., (2.8855).toFixed(3)
 * returns "2.885" instead of "2.886" because 2.8855 is stored as
 * ~2.88549999... in float. The exponent-shift trick (string concat
 * "Xe3" then Math.round then "Ye-3") avoids this because Math.round
 * rounds half-up at integer boundaries reliably.
 *
 * Bug surfaced by Michael Wallace 2026-05-09: P6 Pos. RS - Avg. Size
 * cells with N=2 originations rendered "2.885" / "2.099" when the
 * mathematically correct values were "2.886" / "2.100".
 */
function roundHalfUp(value: number, decimals: number): number {
  // Half AWAY from zero, as Excel's ROUND does: Math.round alone takes -0.2395 to -0.239, and the
  // negative rate-spread averages print signed (-0.240), the way Michael's tables show them.
  const magnitude = Number(Math.round(Number(Math.abs(value) + 'e' + decimals)) + 'e-' + decimals);
  return value < 0 ? -magnitude : magnitude;
}

/** Format a rate for display: "NA" if no data, currency / configurable decimals for means, 2 for incidence */
function fmtRate(rate: number, total: number, isMean?: boolean, decimals = 3, currency?: boolean): string {
  if (total === 0) return 'NA';
  // Half-up first: Intl rounds the binary value, so $5,557.425 would print as $5,557.42.
  if (isMean && currency) return USD.format(roundHalfUp(rate, 2));
  if (isMean) return roundHalfUp(rate, decimals).toFixed(decimals);
  return `${roundHalfUp(rate * 100, 2).toFixed(2)}%`;
}

/**
 * The Total column (Gail Berasley / Michael Wallace, 2026-10-21): the group and its control group across
 * all five purposes, summed from the purpose cells as the Word export does (fldMatrixTable.ts
 * totalMatrixCell). Incidence = summed numerator over summed denominator; mean = loan-weighted mean.
 * Not tested for significance, so no badge and no disparity colouring.
 */
export function FldTotalBlock({ cells, isMeanComparison, meanDecimals = 3, meanUnit, meanCurrency, rateLabel = 'PBG', controlLabel = 'CG' }: {
  cells: CellData[];
  isMeanComparison?: boolean;
  meanDecimals?: number;
  meanUnit?: string;
  meanCurrency?: boolean;
  rateLabel?: string;
  controlLabel?: string;
}) {
  const sum = (pick: (c: CellData) => number) => cells.reduce((s, c) => s + pick(c), 0);
  const count = sum((c) => c.count);
  const total = sum((c) => c.total);
  const controlCount = sum((c) => c.controlCount);
  const controlTotal = sum((c) => c.controlTotal);
  if (total === 0 && controlTotal === 0) {
    return <div className="p-2 text-center text-xs text-ink-4 bg-panel-2 rounded border border-rule">—</div>;
  }
  const rate = isMeanComparison
    ? (total > 0 ? sum((c) => c.rate * c.count) / total : 0)
    : (total > 0 ? count / total : 0);
  const controlRate = isMeanComparison
    ? (controlTotal > 0 ? sum((c) => c.controlRate * c.controlCount) / controlTotal : 0)
    : (controlTotal > 0 ? controlCount / controlTotal : 0);
  const pbg = fmtRate(rate, total, isMeanComparison, meanDecimals, meanCurrency);
  const cg = fmtRate(controlRate, controlTotal, isMeanComparison, meanDecimals, meanCurrency);
  const unit = (n: number) => (meanUnit && n > 0 ? ` ${meanUnit}` : '');
  return (
    <div className="w-full p-2 rounded border border-rule bg-panel-2 text-left text-xs">
      <div className="text-ink-3">
        {isMeanComparison ? `${rateLabel}: ${count} (${pbg}${unit(total)})` : `${rateLabel}: ${count}/${total} (${pbg})`}
      </div>
      <div className="text-ink-3">
        {isMeanComparison ? `${controlLabel}: ${controlCount} (${cg}${unit(controlTotal)})` : `${controlLabel}: ${controlCount}/${controlTotal} (${cg})`}
      </div>
    </div>
  );
}

export default function FldCellBlock({ cell, isMeanComparison, meanDecimals = 3, meanUnit, meanCurrency, rateLabel = 'PBG', controlLabel = 'CG', onClick }: FldCellBlockProps) {
  const hasData = cell.total > 0 || cell.controlTotal > 0;

  if (!hasData) {
    return (
      <div className="p-2 text-center text-xs text-ink-4 bg-panel-2 rounded border border-rule">
        —
      </div>
    );
  }

  const bgClass = cell.noComparison
    ? 'bg-panel border-rule'
    : !cell.hasDisparity
    ? 'bg-green-50 border-green-100'
    : cell.riskLevel === 'HIGH'
      ? 'bg-red-50 border-red-200'
      : cell.riskLevel === 'MEDIUM'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-panel border-rule';

  const pbgRate = fmtRate(cell.rate, cell.total, isMeanComparison, meanDecimals, meanCurrency);
  const cgRate = fmtRate(cell.controlRate, cell.controlTotal, isMeanComparison, meanDecimals, meanCurrency);

  // Format the parenthetical for each row.
  //   Incidence (U1 / U3 / R / S): show rate % (e.g., "0.00%"). Sample size goes
  //     inline as "PBG: 0/6 (0.00%)" so the user sees the denominator at a glance
  //     per Wallace 2026-04-27 feedback.
  //   Mean comparison (U2 / P4): show value + unit (e.g., "37 avg. days").
  const fmtPbgLine = isMeanComparison
    ? `${rateLabel}: ${cell.count} (${pbgRate}${meanUnit && cell.total > 0 ? ` ${meanUnit}` : ''})`
    : `${rateLabel}: ${cell.count}/${cell.total} (${pbgRate})`;
  const fmtCgLine = isMeanComparison
    ? `${controlLabel}: ${cell.controlCount} (${cgRate}${meanUnit && cell.controlTotal > 0 ? ` ${meanUnit}` : ''})`
    : `${controlLabel}: ${cell.controlCount}/${cell.controlTotal} (${cgRate})`;

  // For mean comparison, the displayed Diff must equal (displayed PBG) − (displayed CG) so the card
  // is internally consistent — an examiner subtracting the two rounded rows gets the Diff. Computing
  // it from the raw means and rounding separately produced e.g. "Diff: 10" under rows reading 31 and
  // 20 (Michael Wallace, U2 processing time, 2026-06-07). The raw cell.difference still drives the
  // statistical disparity/significance logic upstream; this only affects display.
  const meanDiffDecimals = meanCurrency ? 2 : meanDecimals;
  const meanDiffValue = roundHalfUp(cell.rate, meanDiffDecimals) - roundHalfUp(cell.controlRate, meanDiffDecimals);
  const meanDiffDisplay = meanCurrency
    ? USD.format(meanDiffValue)
    : `${meanDiffValue.toFixed(meanDecimals)}${meanUnit ? ` ${meanUnit}` : ''}`;

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full p-2 rounded border text-left text-xs transition-shadow hover:shadow-md',
        bgClass,
        onClick && 'cursor-pointer',
      )}
    >
      <div className="flex items-center justify-end mb-1">
        <SignificanceBadge level={cell.significanceLevel as any} />
      </div>
      <div className="text-ink-3">{fmtPbgLine}</div>
      <div className="text-ink-3">{fmtCgLine}</div>
      {cell.hasDisparity && (
        <div className={clsx('text-xs mt-0.5', cell.riskLevel === 'HIGH' ? 'text-red-600 font-medium' : 'text-ink-3')}>
          Diff: {isMeanComparison
            ? meanDiffDisplay
            : `${cell.difference.toFixed(2)}pp`}
        </div>
      )}
      {cell.sampleSizeWarning && (
        <div className="text-amber-600 text-[10px] mt-0.5">Small sample</div>
      )}
    </button>
  );
}
